/**
 * Authorization Manager for iframe
 *
 * Manages user authorizations for different projects/applications
 * Implements the "User Authorization Per Project" security model
 */

import { buildAuthorizationTemplateData, TemplateEngine, TemplateLoader } from '../templates'
import { fetchProjectCard, type ProjectCard } from './project-card'
import type { ProjectInfo, UserAuthorization } from './types'




export class AuthorizationManager {
  private readonly STORAGE_KEY_PREFIX = 'auth'

  constructor() {
    console.log('[iframe][Auth] Initialized')
  }

  async isTrustedAuthorizedApp(projectId: string): Promise<boolean> {
    const card = await fetchProjectCard(projectId)
    return card?.trustedAuthorizedApp === true
  }

  async getProjectMetadata(projectId: string): Promise<ProjectCard | null> {
    return fetchProjectCard(projectId)
  }

  /**
   * Check if user has authorized a project
   */
  async checkAuthorization(userId: string, projectId: string): Promise<boolean> {
    const key = this.getAuthKey(userId, projectId)
    const authData = localStorage.getItem(key)

    if (!authData) {
      return false
    }

    try {
      const auth: UserAuthorization = JSON.parse(authData)

      // Check if authorization is still valid (not expired)
      // TODO: Add expiration logic if needed

      console.log(`[iframe][Auth] User ${userId} authorized for project ${projectId}`)
      return true
    } catch (error) {
      console.error('[iframe][Auth] Failed to parse authorization:', error)
      return false
    }
  }

  /**
   * Store user authorization for a project
   */
  async storeAuthorization(userId: string, projectId: string, origin: string): Promise<void> {
    const auth: UserAuthorization = {
      userId,
      projectId,
      authorizedAt: Date.now(),
      origin
    }

    const key = this.getAuthKey(userId, projectId)
    localStorage.setItem(key, JSON.stringify(auth))

    console.log(`[iframe][Auth] ✅ Stored authorization: ${userId} -> ${projectId}`)
  }

  /**
   * Revoke user authorization for a project
   */
  async revokeAuthorization(userId: string, projectId: string): Promise<void> {
    const key = this.getAuthKey(userId, projectId)
    localStorage.removeItem(key)

    console.log(`[iframe][Auth] ✅ Revoked authorization: ${userId} -> ${projectId}`)
  }

  /**
   * Get all authorizations for a user
   */
  async getUserAuthorizations(userId: string): Promise<UserAuthorization[]> {
    const authorizations: UserAuthorization[] = []
    const prefix = `${this.STORAGE_KEY_PREFIX}.${userId}.`

    // Iterate through localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        try {
          const authData = localStorage.getItem(key)
          if (authData) {
            const auth: UserAuthorization = JSON.parse(authData)
            authorizations.push(auth)
          }
        } catch (error) {
          console.error(`[iframe][Auth] Failed to parse auth for key ${key}:`, error)
        }
      }
    }

    return authorizations
  }

  /**
   * Request user authorization with consent screen
   */
  async requestAuthorization(
    project: ProjectInfo,
    origin: string,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): Promise<boolean> {
    // Request parent to show iframe
    this.showIframe()

    // Fetch project metadata
    const metadata = await fetchProjectCard(project.id)

    return new Promise((resolve) => {
      const modal = this.createAuthorizationModal(project, origin, metadata, userProfile)

      const authorizeBtn = modal.querySelector('.authorize-btn') as HTMLButtonElement
      const cancelBtn = modal.querySelector('.cancel-btn') as HTMLButtonElement

      const cleanup = (result: boolean) => {
        console.log(`[iframe][Auth] Cleaning up modal, result: ${result}`)
        modal.remove()
        // Request parent to hide iframe
        this.hideIframe()
        console.log(`[iframe][Auth] Resolving with result: ${result}`)
        resolve(result)
      }

      authorizeBtn?.addEventListener('click', (e) => {
        console.log(`[iframe][Auth] Authorize button clicked, isTrusted: ${e.isTrusted}`)
        if (e.isTrusted) {
          console.log(`[iframe][Auth] User authorized project: ${project.id}`)
          cleanup(true)
        } else {
          console.warn(`[iframe][Auth] Untrusted click event ignored`)
        }
      })

      cancelBtn?.addEventListener('click', () => {
        console.log(`[iframe][Auth] Cancel button clicked`)
        console.log(`[iframe][Auth] User denied authorization for project: ${project.id}`)
        cleanup(false)
      })

      // Add modal to body
      document.body.appendChild(modal)
    })
  }

  /**
   * Show iframe (notify parent)
   */
  private showIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_SHOW_IFRAME', timestamp: Date.now() }, '*')
      console.log('[iframe][Auth] Requested parent to show iframe')
    }
  }

  /**
   * Hide iframe (notify parent)
   */
  private hideIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_HIDE_IFRAME', timestamp: Date.now() }, '*')
      console.log('[iframe][Auth] Requested parent to hide iframe')
    }
  }

  /**
   * Create authorization consent modal
   */
  private createAuthorizationModal(
    project: ProjectInfo,
    origin: string,
    metadata: ProjectCard | null,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): HTMLElement {
    const modal = document.createElement('div')

    // Build template data
    const templateData = buildAuthorizationTemplateData(project, origin, metadata, userProfile)

    // Load and render template
    const template = TemplateLoader.getTemplate('authorization')
    const populatedHTML = TemplateEngine.render(template, templateData)

    // Set modal content
    modal.innerHTML = populatedHTML

    return modal
  }

  /**
   * Get storage key for authorization
   */
  private getAuthKey(userId: string, projectId: string): string {
    return `${this.STORAGE_KEY_PREFIX}.${userId}.${projectId}`
  }

  /**
   * Clear all authorizations for a user (for logout/cleanup)
   */
  async clearUserAuthorizations(userId: string): Promise<void> {
    const prefix = `${this.STORAGE_KEY_PREFIX}.${userId}.`
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))

    console.log(`[iframe][Auth] ✅ Cleared ${keysToRemove.length} authorizations for user ${userId}`)
  }

  /**
   * Get authorization statistics
   */
  getStats(): { total: number; byUser: Record<string, number> } {
    const stats: { total: number; byUser: Record<string, number> } = {
      total: 0,
      byUser: {}
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
        stats.total++

        // Extract user ID from key: "auth.{userId}.{projectId}"
        const parts = key.split('.')
        if (parts.length >= 2) {
          const userId = parts[1]
          stats.byUser[userId] = (stats.byUser[userId] || 0) + 1
        }
      }
    }

    return stats
  }
}
